// src/services/analytics.js
import { initializeApp } from "firebase/app";
import { getAnalytics, logEvent, setUserId, setUserProperties } from "firebase/analytics";
import CONFIG_VARS from "../constants/configuration";

const firebaseConfig = {
  apiKey: "AIzaSyB_b69_ihjMQBGbx26x_6tqq5RQvLgkumY",
  authDomain: "il-genius.firebaseapp.com",
  projectId: "il-genius",
  storageBucket: "il-genius.firebasestorage.app",
  messagingSenderId: "776432510262",
  appId: "1:776432510262:web:3f4a298d46b5d148a2bf97"
};

const ANALYTICS_ENABLED_ENVS = {
  production: true,
  prodreplica: true,
  preprod: false,
  staging: false
};

class AnalyticsService {
  constructor() {
    this.analytics = null;
    this.isEnabled = false;
    this.currentEnv = CONFIG_VARS.environment;
    this.FORCE_CONSOLE_LOGS = ANALYTICS_ENABLED_ENVS[this.currentEnv] || false;
  }

  initialize() {
    const shouldEnable = ANALYTICS_ENABLED_ENVS[this.currentEnv] || false;

    if (!shouldEnable) {
      if (this.FORCE_CONSOLE_LOGS) {
        console.log(`[Analytics] Disabled for environment: ${this.currentEnv}`);
      }
      this.isEnabled = false;
      return;
    }

    try {
      const app = initializeApp(firebaseConfig);
      this.analytics = getAnalytics(app);
      this.isEnabled = true;
      if (this.FORCE_CONSOLE_LOGS) {
        console.log(`[Analytics] Initialized successfully for: ${this.currentEnv}`);
      }
    } catch (error) {
      console.error("[Analytics] Initialization failed:", error);
      this.isEnabled = false;
    }
  }

  setUser(userId, userProperties = {}) {
    if (!this.isEnabled) {
      return;
    }

    try {
      if (this.FORCE_CONSOLE_LOGS) {
        console.log(`[Analytics] setUser: ${userId}`, userProperties);
      }
      setUserId(this.analytics, userId);
      if (Object.keys(userProperties).length > 0) {
        setUserProperties(this.analytics, userProperties);
      }
    } catch (error) {
      console.error("[Analytics] setUser failed:", error);
    }
  }

  clearUser() {
    if (!this.isEnabled) return;

    try {
      if (this.FORCE_CONSOLE_LOGS) {
        console.log("[Analytics] clearUser");
      }
      setUserId(this.analytics, null);
    } catch (error) {
      console.error("[Analytics] clearUser failed:", error);
    }
  }

  logEvent(eventName, parameters = {}) {
    if (this.FORCE_CONSOLE_LOGS) {
      console.log(`[Analytics] Event: ${eventName}`, {
        ...parameters,
        analytics_enabled: this.isEnabled,
        environment: this.currentEnv
      });
    }

    if (!this.isEnabled) return;

    try {
      const enrichedParams = {
        ...parameters,
        environment: this.currentEnv
      };

      logEvent(this.analytics, eventName, enrichedParams);
    } catch (error) {
      console.error(`[Analytics] Failed to log ${eventName}:`, error);
    }
  }

  // Login Events
  loginPageViewed() {
    this.logEvent("login_page_viewed");
  }

  loginMethodSelected(method) {
    this.logEvent("login_method_selected", { method });
  }

  loginAttempted(method) {
    this.logEvent("login_attempted", { method });
  }

  loginSuccess(userId, loginMethod) {
    this.logEvent("login_success", {
      user_id: userId,
      login_method: loginMethod
    });
    this.setUser(userId, { login_method: loginMethod });
  }

  loginFailed(method, errorReason, attemptNumber) {
    this.logEvent("login_failed", {
      method,
      error_reason: errorReason,
      attempt_number: attemptNumber
    });
  }

  loginApiTimeout(responseTimeMs) {
    this.logEvent("login_api_timeout", {
      response_time_ms: responseTimeMs
    });
  }

  // Deeplink Events
  deeplinkAutoLoginSuccess(url, userId) {
    this.logEvent("deeplink_auto_login_success", {
      url,
      User_ID: userId
    });
  }

  deeplinkAutoLoginFailed(errorReason, url, userId) {
    this.logEvent("deeplink_auto_login_failed", {
      error_reason: errorReason,
      url,
      User_ID: userId
    });
  }

  // Dashboard Events
  dashboardViewed() {
    this.logEvent("dashboard_viewed");
  }

  takeTestClicked(testId) {
    this.logEvent("take_test_clicked", {
      test_id: testId
    });
  }

  // Exam Landing Events
  examLandingViewed(testId, source) {
    this.logEvent("exam_landing_viewed", {
      test_id: testId,
      source: source || "organic"
    });
  }

  attemptNowClicked(testId, source) {
    this.logEvent("attempt_now_clicked", {
      test_id: testId,
      source: source
    });
  }

  resultsPageViewed(testId, timeSpentSecs) {
    this.logEvent("results_page_viewed", {
      test_id: testId,
      time_spent_secs: timeSpentSecs
    });
  }

  // Forgot Password
  forgotPasswordClicked(userName) {
    this.logEvent("forgot_password_clicked", {
      UserName: userName
    });
  }

  // Deeplink Opened
  deeplinkOpened(eventId) {
    this.logEvent("deeplink_opened", {
      event_id: eventId
    });
  }

  // Scheduled Tab Viewed
  scheduledTabViewed(hasLiveTest) {
    this.logEvent("scheduled_tab_viewed", {
      has_live_test: hasLiveTest
    });
  }

  // Dashboard Events API Failed
  dashboardEventsApiFailed(httpStatus, responseTimeMs) {
    this.logEvent("dashboard_events_api_failed", {
      http_status: httpStatus,
      response_time_ms: responseTimeMs
    });
  }

  // Dashboard Rendered Empty
  dashboardRenderedEmpty(apiFailed) {
    this.logEvent("dashboard_rendered_empty", {
      api_failed: apiFailed
    });
  }

  // Report Status API Failed
  reportStatusApiFailed(httpStatus, pollAttemptNumber) {
    this.logEvent("report_status_api_failed", {
      http_status: httpStatus,
      poll_attempt_number: pollAttemptNumber
    });
  }

  // Report Stuck Loading
  reportStuckLoading(loadTime, totalWaitMs) {
    this.logEvent("report_stuck_loading", {
      load_time: loadTime,
      total_wait_ms: totalWaitMs
    });
  }

  // Certificate Download Clicked
  certificateDownloadClicked(testId, score) {
    this.logEvent("certificate_download_clicked", {
      test_id: testId,
      score: score
    });
  }

  // Advance Report Viewed
  advanceReportViewed(loadTime, timeSpentMs) {
    this.logEvent("advance_report_viewed", {
      load_time: loadTime,
      Time_Spent_ms: timeSpentMs
    });
  }

  // View Solutions Clicked
  viewSolutionsClicked(testId) {
    this.logEvent("view_solutions_clicked", {
      test_id: testId
    });
  }

  // Genius marketing
  geniusStudentRegistrationIntent(params = {}) {
    this.logEvent("genius_student_registration_intent", params);
  }

  geniusSchoolRegistrationIntent(params = {}) {
    this.logEvent("genius_school_registration_intent", params);
  }

  geniusBrochureDownload(params = {}) {
    this.logEvent("genius_brochure_download", params);
  }

  geniusGetInTouch(params = {}) {
    this.logEvent("genius_get_in_touch", params);
  }
}

const analyticsService = new AnalyticsService();
export default analyticsService;
